import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Box, Stack, Typography, InputBase, CircularProgress } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { debounce } from 'lodash';
import { shallowEqual } from 'react-redux';
import { VirtualItem } from '@tanstack/react-virtual';

import { Close as CloseIcon, NewSearch as SearchIcon, ArrowLeft as BackIcon, LoadingWrapper } from '@linetweet/linetweet-ui';

import { SearchCalendarEvent } from '../../types';
import { searchCalendarEvents } from '../../store/appointments/thunks';
import { resetSearch } from '../../store/appointments/slice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { TimeHelper } from '../../utils';

import styles from './index.module.scss';
import { SearchItem } from './subcomponents/SearchItem';
import { NEXT_PAGE_LOADER_KEY, VirtualList } from '../commons';
import { selectCurrentStore, selectStoreServices } from '../../store/store/selectors';

export type SearchFormProps = {
  onClick: (params: { searchCalendarEvent: SearchCalendarEvent; isPastEvent: boolean }) => void;
};

const SEARCH_ITEMS_PER_PAGE = 6;
const VIRTUAL_ITEM_HEIGHT = 200;

export function SearchForm({ onClick }: SearchFormProps) {
  const appointmentsState = useAppSelector((state) => state.appointments);
  const store = useAppSelector(selectCurrentStore);
  const allServices = useAppSelector(selectStoreServices, shallowEqual);

  const [searchValue, setSearchValue] = useState('');
  const [prevSearch, setPrevSearch] = useState<{ page: number, value: string }>({ page: 1, value: '' });
  const dispatch = useAppDispatch();
  const intl = useIntl();

  const navigate = useNavigate();

  const handleBack = useCallback(() => {
    navigate('/');
    dispatch(resetSearch());
  }, []);

  const handleCloseSearchBar = useCallback(() => {
    setSearchValue('');
  }, []);

  const debouncedSearchAppointments = useCallback(async (search: string, page: number) => {
    const now = TimeHelper.getNowDateUnix(store.timezone);
    const startOfDay = TimeHelper.getStartOfDay(now, store.timezone);
    const to = TimeHelper.addDaysTz(startOfDay, 8, store.timezone);
    const fromString = TimeHelper.unixToStandardFormat(startOfDay, store.timezone);
    const toString = TimeHelper.unixToStandardFormat(to, store.timezone);

    const query = {
      term: '',
      from: fromString,
      page,
      itemsPerPage: SEARCH_ITEMS_PER_PAGE,
      to: toString,
      storeId: store.id,
      enterpriseId: store.enterpriseId,
      timezone: store.timezone,
    };
    if (search) {
      const fromPast = TimeHelper.subtractMonths(startOfDay, 6, store.timezone);
      const fromPastString = TimeHelper.unixToStandardFormat(fromPast, store.timezone);
      const toPast = TimeHelper.addDaysTz(startOfDay, 365, store.timezone);
      const toPastString = TimeHelper.unixToStandardFormat(toPast, store.timezone);
      const trimmed = search.trim();
      if (trimmed.length >= 3) {
        query.term = trimmed;
        query.from = fromPastString;
        query.to = toPastString;
      }
    }
    await dispatch(searchCalendarEvents(query));
    setPrevSearch({ value: search, page });
  }, [store]);

  const debouncedDispatch = useMemo(() => debounce(debouncedSearchAppointments, 500), [store]);

  useEffect(() => {
    if (appointmentsState.search.loading) {
      return;
    }

    const valueChanged: boolean = searchValue !== prevSearch.value;
    const isInitialLoad: boolean = !appointmentsState.search.data && !appointmentsState.search.error;
    if (valueChanged || isInitialLoad) {
      debouncedDispatch(searchValue, 1);
    }
  }, [searchValue, appointmentsState.search.loading, appointmentsState.search.data, store, prevSearch]);

  const calendarEvents = useMemo(() => {
    if (!appointmentsState.search.data || appointmentsState.search.data.length === 0) {
      return [];
    }

    const sorted = appointmentsState.search.data.map((calendarEvent) => ({
      ...calendarEvent,
      dateTime: TimeHelper.toDayjs(calendarEvent.date, store.timezone).startOf('day').add(calendarEvent.time, 'minutes').valueOf(),
    })).sort((a, b) => b.dateTime - a.dateTime);

    // using this to figure out first and last items in list
    let prevItemDayDay: number;

    const dayData: SearchCalendarEvent[] = [];
    sorted.forEach((calendarEvent) => {
      const day = TimeHelper.toDayjs(calendarEvent.date, store.timezone).startOf('day');
      const dayStart = day.valueOf();
      let isFirstInDay = false;
      if (prevItemDayDay && prevItemDayDay === dayStart) {
        dayData[dayData.length - 1].isLastInDay = false;
        // reset last in day
      } else {
        // day is changed, set first in day
        prevItemDayDay = dayStart;
        isFirstInDay = true;
      }
      dayData.push({
        day,
        dateTime: calendarEvent.dateTime,
        isFirstInDay,
        isLastInDay: true,
        calendarEvent,
      });
    });

    return dayData;
  }, [appointmentsState.search.data, prevSearch]);

  const hasNextPage = useMemo(() => (
    !appointmentsState.search.loading && appointmentsState.search.hasNextPage
  ), [appointmentsState.search.loading && appointmentsState.search.hasNextPage]);

  const isFetchingNextPage = useMemo(
    () => {
      if (!appointmentsState.search.hasNextPage) {
        return false;
      }
      return appointmentsState.search.loading;
    },
    [appointmentsState.search],
  );

  const fetchNextPage = useCallback(() => {
    debouncedDispatch(searchValue, prevSearch.page + 1);
  }, [searchValue, prevSearch]);

  const renderRow = useCallback(
    (data: SearchCalendarEvent, index: number, items: SearchCalendarEvent[], virtualItem: VirtualItem) => {
      if (virtualItem.key === NEXT_PAGE_LOADER_KEY) {
        const shouldDisplayLoader = hasNextPage || isFetchingNextPage;
        return (
          <Box
            key={virtualItem.key}
            className={styles.loaderRow}
            sx={{ height: `${VIRTUAL_ITEM_HEIGHT}px` }}
          >
            {shouldDisplayLoader && (
              <CircularProgress disableShrink />
            )}
          </Box>
        );
      }

      return (
        <SearchItem appointmentData={data} allServices={allServices} onClick={onClick} timezone={store.timezone} />
      );
    },
    [allServices, onClick, store, hasNextPage, isFetchingNextPage],
  );

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Stack direction="row" justifyContent="space-between">
          <IconButton onClick={handleBack}>
            <BackIcon className={styles.backIcon} />
          </IconButton>
          <Stack direction="row" justifyContent="flex-start" alignItems="center" className={styles.titleContainer}>
            <Typography className={styles.title}>{intl.formatMessage({ id: 'search.title' })}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="flex-start" alignItems="center" className={styles.searchContainer}>
            <SearchIcon height={16} width={24} className={styles.searchIcon} />
            <InputBase
              autoFocus
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder={`${intl.formatMessage({ id: 'common.search' })}...`}
              className={styles.input}
            />
            <Box className={styles.closeIcon}>
              <CloseIcon height={20} width={20} onClick={handleCloseSearchBar} />
            </Box>
          </Stack>
        </Stack>
      </Box>
      <Box className={styles.searchResultsContainer}>
        <LoadingWrapper loading={!appointmentsState.search.error && !appointmentsState.search.data}>
          {calendarEvents.length > 0 ? (
            <VirtualList<SearchCalendarEvent>
              parentHeight="calc(100vh - 64px)"
              estimateSize={VIRTUAL_ITEM_HEIGHT}
              hideScroll
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
              data={calendarEvents}
              renderRow={renderRow}
            />
          ) : (
            <Typography className={styles.noResultsText}>
              {intl.formatMessage({ id: 'search.noResults' })}
            </Typography>
          )}
        </LoadingWrapper>
      </Box>
    </Box>
  );
}
